import { useState, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Exécution d'une action de masse : état occupé, rapport, rafraîchissement.
 *
 * La confirmation ne passe plus par ici. Elle appartient au dialogue qui a servi à
 * choisir l'action (`BulkEditDialog`), qui sait nommer la cible, afficher un
 * avertissement et proposer Annuler — ce qu'un `window.confirm()` ne fait pas.
 *
 * Règle d'affichage : un `failed` non vide n'est pas une erreur HTTP, c'est un succès
 * partiel, normal dès qu'on parle à du matériel — le rapport détaillé s'ouvre alors
 * toujours. Un lot 100 % réussi se signale par un toast, SAUF si l'appelant demande
 * `alwaysReport` : sur un geste irréversible ou physique, l'admin doit pouvoir lire le
 * compte rendu au lieu de courir après un toast qui s'efface.
 *
 * @param {function} onDone rappelé après chaque action (rechargement de la liste)
 */
export default function useBulkAction(onDone) {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const clearReport = useCallback(() => setReport(null), []);

  const run = useCallback(async (fn, { success, alwaysReport } = {}) => {
    setBusy(true);
    try {
      const rep = await fn();
      if (rep?.failed?.length || (alwaysReport && rep)) setReport(rep);
      else toast.success(typeof success === 'function' ? success(rep) : (success || 'Action effectuée'));
      onDone?.();
      return rep;
    } catch (err) {
      // Une action peut refuser AVANT d'appeler l'API (formulaire vide, par exemple).
      // Son message est le seul utile : sans ce repli, il disparaissait derrière un
      // « Échec de l'action en masse » qui ne dit pas quoi corriger.
      const detail = err.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' && detail)
        || err.message
        || "Échec de l'action en masse",
      );
      return null;
    } finally {
      setBusy(false);
    }
  }, [onDone]);

  return { busy, report, clearReport, run };
}
